import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useQuery } from '@tanstack/react-query';
import axiosClient from '../api/axiosClient';
import { authEnabled } from './config';

interface AppConfigResponse {
  storyblokSpaceId?: string;
  contentEnabled?: boolean;
  contentAuthoringEnabled?: boolean;
}

/**
 * Fetches /api/me once at boot to seed window.__SUNNY_CONFIG__ with
 * server-side env values such as storyblokSpaceId.
 *
 * WHY: in AUTH_ENABLED=false mode useCurrentUser short-circuits to a
 * LOCAL_DEV_ME synthetic identity and never calls /api/me, so
 * window.__SUNNY_CONFIG__ would remain empty and StoryblokLink would
 * fall back to the generic Storyblok home URL.
 *
 * SAFETY: the axios client redirects to Auth0 on any 401. To avoid an
 * infinite loop we only fetch when it's safe:
 *   - AUTH_ENABLED=false  → fetch immediately (no auth required in dev)
 *   - AUTH_ENABLED=true   → wait until Auth0 confirms the user is
 *                           authenticated before calling /api/me
 *
 * Renders nothing — purely a side-effect component.
 */
export default function AppBootstrap() {
  const { isAuthenticated } = useAuth0();

  // Safe to fetch when: auth is disabled (local dev) OR user is already
  // authenticated. Never fetch while the user is unauthenticated with auth
  // enabled — /api/me would 401 and trigger the logout-redirect loop.
  const canFetch = !authEnabled || isAuthenticated;

  const { data } = useQuery<AppConfigResponse>({
    queryKey: ['app-bootstrap-config'],
    queryFn: async () => {
      const res = await axiosClient.get<AppConfigResponse>('/api/me');
      return res.data;
    },
    staleTime: Infinity,
    retry: false,
    enabled: canFetch,
  });

  useEffect(() => {
    if (data) {
      (window as any).__SUNNY_CONFIG__ = {
        ...((window as any).__SUNNY_CONFIG__ ?? {}),
        ...(data.storyblokSpaceId ? { storyblokSpaceId: data.storyblokSpaceId } : {}),
      };
    }
  }, [data]);

  return null;
}
